// src/components/inventory/StockMovementModal.js
// --- UPDATE (Inventory Enterprise v1) — NEW FILE -----------------------------------------
// Receive · Issue (to a work order, asset or vehicle) · Return · Count · Adjust · Transfer.
// Transactional on the server (row lock; cannot issue more than is in stock). Shows the new
// quantity before saving.
// --- END UPDATE ---------------------------------------------------------------------------
// --- UPDATE (Inventory MOW+ v2) -----------------------------------------------------------
//   • FlowSense AI check of the result: below the ML re-order point / out of stock after an
//     issue, how much FlowSense suggests ordering, receipts that create overstock.
//   • Live: if the same item is moved by someone else while this form is open (WebSocket), the
//     current quantity updates and a note says so — the preview never uses a stale number.
//   • Enter saves; the movement types explain themselves for new officers.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Button, FormControl, FormLabel, Input, Select,
    NumberInput, NumberInputField, Textarea, VStack, HStack, Text, Alert, AlertIcon, RadioGroup, Radio, Stack, Switch, Badge, useColorModeValue, Box,
} from '@chakra-ui/react';
import inventoryIntelApi from '../../services/inventoryIntelApi';
// --- UPDATE (MOW+ v2)
import { Icon } from '@chakra-ui/react';
import { MdAutoAwesome } from 'react-icons/md';
import useInventoryLive from '../../hooks/useInventoryLive';
// --- END UPDATE

const TYPES = [
    { id: 'receive', label: 'Receive', help: 'Stock arrived (delivery / purchase order).' },
    { id: 'issue', label: 'Issue', help: 'Taken out for a work order, an asset or a vehicle.' },
    { id: 'return', label: 'Return', help: 'Unused parts brought back.' },
    { id: 'count', label: 'Count', help: 'Cycle count — set the counted quantity.' },
    { id: 'adjust', label: 'Adjust', help: 'Damage, loss or correction.' },
    { id: 'transfer', label: 'Transfer', help: 'Move the item to another location.' },
];

function StockMovementModal({ isOpen, onClose, item, lookups, defaultType = 'issue', defaultTarget = null, onDone }) {
    const [type, setType] = useState(defaultType);
    const [qty, setQty] = useState('');
    const [direction, setDirection] = useState('down');
    const [targetType, setTargetType] = useState('');
    const [targetId, setTargetId] = useState('');
    const [workOrderId, setWorkOrderId] = useState('');
    const [unitCost, setUnitCost] = useState('');
    const [updatePrice, setUpdatePrice] = useState(false);
    const [locationTo, setLocationTo] = useState('');
    const [reference, setReference] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const muted = useColorModeValue('gray.500', 'gray.400');
    const previewBg = useColorModeValue('blue.50', 'whiteAlpha.100');
    const aiBg = useColorModeValue('teal.50', 'whiteAlpha.100'); // --- UPDATE
    const [liveQty, setLiveQty] = useState(null); // --- UPDATE
    const [ai, setAi] = useState(null); // --- UPDATE

    useEffect(() => {
        if (!isOpen) return;
        setType(defaultType); setQty(''); setDirection('down'); setTargetType(defaultTarget?.type || ''); setTargetId(defaultTarget?.id != null ? String(defaultTarget.id) : '');
        setWorkOrderId(''); setUnitCost(''); setUpdatePrice(false); setLocationTo(''); setReference(''); setReason(''); setError(null);
        setLiveQty(null); // --- UPDATE
    }, [isOpen, defaultType, defaultTarget?.type, defaultTarget?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- --- UPDATE (MOW+ v2): values, not the object — a parent re-render (live refresh) no longer wipes a half-filled form

    // --- UPDATE (MOW+ v2): FlowSense numbers for the item (re-order point, EOQ, cover) + live quantity
    const loadAi = React.useCallback(async () => {
        if (!item?.id) return;
        try { const r = await inventoryIntelApi.insight(item.id); const i = r?.item || null; setAi(i); if (i && Number(i.quantity) !== Number(item.quantity)) setLiveQty(Number(i.quantity)); } catch (_) { setAi(null); }
    }, [item]);
    useEffect(() => { if (isOpen) { setAi(null); loadAi(); } }, [isOpen, loadAi]);
    useInventoryLive(() => loadAi(), { enabled: !!(isOpen && item?.id), pollMs: 0, filter: (e) => e.item_id == null || String(e.item_id) === String(item?.id) });
    // --- END UPDATE

    const current = Number(liveQty ?? item?.quantity ?? 0); // --- UPDATE: live quantity when it changed
    const n = Number(qty);
    const after = useMemo(() => {
        if (type === 'transfer') return current;
        if (!Number.isFinite(n) || qty === '') return null;
        if (type === 'receive' || type === 'return') return current + n;
        if (type === 'issue') return current - n;
        if (type === 'count') return n;
        return current + (direction === 'down' ? -n : n);
    }, [type, n, qty, current, direction]);
    // --- UPDATE: FlowSense check of the result
    const rop = ai?.effective_reorder_point ?? item?.effective_reorder_point ?? null;
    const aiNote = useMemo(() => {
        if (after === null || type === 'transfer') return null;
        if (after === 0 && current > 0) return { level: 'error', text: `This leaves ${item?.name || 'the item'} out of stock.${ai?.eoq ? ` FlowSense suggests ordering ${ai.eoq}.` : ''}` };
        if (rop != null && after < rop && current >= rop) return { level: 'warning', text: `After this, stock (${after}) is below the FlowSense re-order point (${rop}).${ai?.eoq ? ` Suggested order: ${ai.eoq}.` : ''}` };
        if (rop != null && after < rop) return { level: 'warning', text: `Still below the FlowSense re-order point (${rop}).` };
        if (type === 'receive' && ai?.avg_daily_usage > 0 && after / ai.avg_daily_usage > 365) return { level: 'info', text: `That is about ${Math.round(after / ai.avg_daily_usage)} days of stock at the current usage (${ai.avg_daily_usage}/day) — more than a year.` };
        if (ai?.avg_daily_usage > 0) return { level: 'info', text: `About ${Math.round(after / ai.avg_daily_usage)} days of cover at ${ai.avg_daily_usage}/day.` };
        return null;
    }, [after, type, current, rop, ai, item]);
    // --- END UPDATE
    const invalid = type === 'transfer' ? !locationTo.trim() : (qty === '' || !Number.isFinite(n) || n < 0 || (['receive', 'issue', 'return'].includes(type) && n === 0) || (after !== null && after < 0));

    const submit = async () => {
        if (invalid) return;
        setBusy(true); setError(null);
        try {
            const body = { type, reason: reason.trim() || undefined, reference: reference.trim() || undefined };
            if (type !== 'transfer') body.quantity = n;
            if (type === 'adjust') body.direction = direction;
            if (type === 'issue' || type === 'return') {
                if (targetType && targetId) { body.target_type = targetType; body.target_id = targetId; }
                if (workOrderId.trim()) body.work_order_id = workOrderId.trim();
            }
            if (type === 'receive' && unitCost !== '') { body.unit_cost = Number(unitCost); body.update_price = updatePrice; }
            if (type === 'transfer') body.location_to = locationTo.trim();
            const r = await inventoryIntelApi.recordMovement(item.id, body);
            onDone?.(r);
            onClose();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const targets = targetType === 'vehicle' ? (lookups?.vehicles || []).map((v) => ({ id: v.id, label: v.display_name }))
        : targetType === 'asset' ? (lookups?.assets || []).map((a) => ({ id: a.id, label: a.name || a.id })) : [];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl">
                <ModalHeader>
                    <Text>Stock movement</Text>
                    <Text fontSize="sm" color={muted} fontWeight="normal">{item?.name}{item?.sku ? ` · ${item.sku}` : ''} — {current} in stock{item?.location ? ` at ${item.location}` : ''}</Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && e.target.tagName !== 'TEXTAREA' && !invalid && !busy) { e.preventDefault(); submit(); } }}>{/* --- UPDATE: Enter saves */}
                    <VStack align="stretch" spacing={4}>
                        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
                        {/* --- UPDATE */}
                        {liveQty !== null && <Alert status="info" borderRadius="lg" fontSize="sm"><AlertIcon />Stock changed while this form was open — it is now {liveQty}.</Alert>}
                        <RadioGroup value={type} onChange={setType}>
                            <Stack direction="row" flexWrap="wrap" spacing={3}>
                                {TYPES.map((t) => <Radio key={t.id} value={t.id}>{t.label}</Radio>)}
                            </Stack>
                        </RadioGroup>
                        <Text fontSize="xs" color={muted}>{TYPES.find((t) => t.id === type)?.help}</Text>
                        {type !== 'transfer' && (
                            <HStack align="flex-end">
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">{type === 'count' ? 'Counted quantity' : 'Quantity'}</FormLabel>
                                    <NumberInput min={0} value={qty} onChange={(v) => setQty(v)}><NumberInputField aria-label="Movement quantity" /></NumberInput>
                                </FormControl>
                                {type === 'adjust' && (
                                    <Select value={direction} onChange={(e) => setDirection(e.target.value)} maxW="150px" aria-label="Adjustment direction">
                                        <option value="down">Decrease</option><option value="up">Increase</option>
                                    </Select>
                                )}
                            </HStack>
                        )}
                        {(type === 'issue' || type === 'return') && (
                            <Box>
                                <HStack align="flex-end">
                                    <FormControl>
                                        <FormLabel fontSize="sm">{type === 'issue' ? 'Issued to' : 'Returned from'}</FormLabel>
                                        <Select value={targetType} onChange={(e) => { setTargetType(e.target.value); setTargetId(''); }} aria-label="Target type">
                                            <option value="">— none —</option><option value="vehicle">Vehicle</option><option value="asset">Asset</option>
                                        </Select>
                                    </FormControl>
                                    {targetType && (
                                        <FormControl>
                                            <FormLabel fontSize="sm">{targetType === 'vehicle' ? 'Vehicle' : 'Asset'}</FormLabel>
                                            <Select value={targetId} onChange={(e) => setTargetId(e.target.value)} placeholder="Select…" aria-label="Target">
                                                {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                                            </Select>
                                        </FormControl>
                                    )}
                                </HStack>
                                <FormControl mt={3}>
                                    <FormLabel fontSize="sm">Work order # (optional)</FormLabel>
                                    <Input value={workOrderId} onChange={(e) => setWorkOrderId(e.target.value)} placeholder="e.g. 10452" aria-label="Work order" />
                                </FormControl>
                            </Box>
                        )}
                        {type === 'receive' && (
                            <HStack align="flex-end">
                                <FormControl>
                                    <FormLabel fontSize="sm">Unit cost (optional)</FormLabel>
                                    <NumberInput min={0} precision={2} value={unitCost} onChange={(v) => setUnitCost(v)}><NumberInputField aria-label="Unit cost" /></NumberInput>
                                </FormControl>
                                <HStack pb={2}><Switch id="upd-price" isChecked={updatePrice} onChange={(e) => setUpdatePrice(e.target.checked)} /><FormLabel htmlFor="upd-price" mb={0} fontSize="sm">Update item price</FormLabel></HStack>
                            </HStack>
                        )}
                        {type === 'transfer' && (
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">New location</FormLabel>
                                <Input value={locationTo} onChange={(e) => setLocationTo(e.target.value)} placeholder="Warehouse / bay / truck" aria-label="New location" />
                            </FormControl>
                        )}
                        <HStack>
                            <FormControl><FormLabel fontSize="sm">Reference</FormLabel><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="PO / delivery note" /></FormControl>
                        </HStack>
                        <FormControl><FormLabel fontSize="sm">Reason / notes</FormLabel><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></FormControl>
                        {type !== 'transfer' && (
                            <HStack p={3} bg={previewBg} borderRadius="lg" justify="space-between">
                                <Text fontSize="sm">New quantity</Text>
                                <HStack><Text fontSize="sm" color={muted}>{current} →</Text><Badge fontSize="md" colorScheme={after !== null && after < 0 ? 'red' : 'blue'} aria-label="New quantity">{after === null ? '—' : after}</Badge></HStack>
                            </HStack>
                        )}
                        {after !== null && after < 0 && <Text fontSize="xs" color="red.500">Not enough stock for this movement.</Text>}
                        {/* --- UPDATE: FlowSense check */}
                        {aiNote && after !== null && after >= 0 && (
                            <HStack bg={aiBg} p={2.5} borderRadius="lg" align="start" spacing={2} role="note" aria-label="FlowSense check">
                                <Icon as={MdAutoAwesome} color={aiNote.level === 'error' ? 'red.400' : aiNote.level === 'warning' ? 'orange.400' : 'teal.400'} mt={0.5} />
                                <Text fontSize="sm">{aiNote.text}</Text>
                            </HStack>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button colorScheme="blue" onClick={submit} isLoading={busy} isDisabled={invalid}>Save movement</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

StockMovementModal.propTypes = { isOpen: PropTypes.bool, onClose: PropTypes.func.isRequired, item: PropTypes.object, lookups: PropTypes.object, defaultType: PropTypes.string, defaultTarget: PropTypes.object, onDone: PropTypes.func };
export default StockMovementModal;
